import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FormGrid } from "@/components/ui/form-grid";
import { Surface } from "@/components/ui/surface";
import { Switch } from "@/components/ui/switch";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/v-stack";
import type { LoadType } from "@/lib/api/loads";
import { getLoadTypes } from "@/lib/api/loads";
import { createLoadType, updateLoadType } from "@/lib/api/catalogs";
import {
  LocalizedNamesFields,
  toLocalizedName,
  type LocalizedNamesDraft,
} from "@/components/admin/catalogs/localized-names";
import { CatalogPageTemplate } from "@/components/admin/catalog-page-template";

type LoadTypeRow = LoadType & {
  refrigeration_mode?: boolean;
  is_dangerous_category?: boolean;
  is_deleted?: boolean;
};

function AdminLoadTypesCatalogPage() {
  const { t } = useTranslation("admin.catalogs.loadTypes");

  return (
    <CatalogPageTemplate<LoadTypeRow>
      title={t("title")}
      tableId="catalog-load-types"
      queryKey={["admin", "catalogs", "load-types"]}
      fetchFn={getLoadTypes}
      columns={[
        {
          key: "name",
          header: t("columns.nameEn"),
          defaultWidth: 200,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              weight="medium"
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.en ?? item.name}
            </Text>
          ),
        },
        {
          key: "nameRu",
          header: t("columns.nameRu"),
          defaultWidth: 160,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.ru ?? "—"}
            </Text>
          ),
        },
        {
          key: "nameUz",
          header: t("columns.nameUz"),
          defaultWidth: 160,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.uz ?? "—"}
            </Text>
          ),
        },
      ]}
      searchFilter={(item, q) => {
        const names = [
          item.name,
          item.names?.en,
          item.names?.ru,
          item.names?.uz,
          item.names?.tr,
          item.names?.uz_cyr,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return names.includes(q);
      }}
      searchPlaceholder={t("searchPlaceholder")}
      emptyMessage={t("noItems")}
      addLabel={t("add")}
      editTitle={t("modalEditTitle")}
      addTitle={t("modalAddTitle")}
      failedToLoadMessage={t("failedToLoad")}
      failedToSaveMessage={t("failedToSave")}
      deleteEndpoint="/load-types"
      editLabel={t("edit")}
      itemToFormData={(item) => ({
        namesDraft: {
          en: item.names?.en ?? item.name ?? "",
          ru: item.names?.ru ?? "",
          uz: item.names?.uz ?? "",
        } as LocalizedNamesDraft,
        refrigerationMode: Boolean(item.refrigeration_mode),
        dangerousCategory: Boolean(item.is_dangerous_category),
      })}
      emptyFormData={() => ({
        namesDraft: { en: "", ru: "", uz: "" } as LocalizedNamesDraft,
        refrigerationMode: false,
        dangerousCategory: false,
      })}
      formDataToPayload={(fd) => ({
        name: (fd.namesDraft as LocalizedNamesDraft).en,
        names: toLocalizedName(fd.namesDraft as LocalizedNamesDraft),
        refrigeration_mode: fd.refrigerationMode as boolean,
        is_dangerous_category: fd.dangerousCategory as boolean,
      })}
      isFormValid={(fd) =>
        Boolean((fd.namesDraft as LocalizedNamesDraft).en.trim())
      }
      createFn={(payload) =>
        createLoadType(payload as Parameters<typeof createLoadType>[0])
      }
      updateFn={(id, payload) =>
        updateLoadType(id, payload as Parameters<typeof updateLoadType>[1])
      }
      renderForm={(formData, setFormData, submitting) => (
        <>
          <LocalizedNamesFields
            value={formData.namesDraft as LocalizedNamesDraft}
            onChange={(next) =>
              setFormData((prev) => ({ ...prev, namesDraft: next }))
            }
            disabled={submitting}
            testIdPrefix="admin-load-type"
          />
          <FormGrid>
            <Surface
              border="default"
              padding="md"
              className="flex items-center justify-between gap-4"
            >
              <VStack gap="none">
                <Text weight="medium">{t("fields.refrigerationMode")}</Text>
                <Text size="xs" color="muted">
                  {t("fields.refrigerationHelper")}
                </Text>
              </VStack>
              <Switch
                checked={formData.refrigerationMode as boolean}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({
                    ...prev,
                    refrigerationMode: checked,
                  }))
                }
                disabled={submitting}
              />
            </Surface>
            <Surface
              border="default"
              padding="md"
              className="flex items-center justify-between gap-4"
            >
              <VStack gap="none">
                <Text weight="medium">{t("fields.dangerousCategory")}</Text>
                <Text size="xs" color="muted">
                  {t("fields.dangerousHelper")}
                </Text>
              </VStack>
              <Switch
                checked={formData.dangerousCategory as boolean}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({
                    ...prev,
                    dangerousCategory: checked,
                  }))
                }
                disabled={submitting}
              />
            </Surface>
          </FormGrid>
        </>
      )}
      testIdPrefix="admin-catalog-load-types"
    />
  );
}

export const Route = createFileRoute("/_app/admin/catalogs/load-types")({
  component: AdminLoadTypesCatalogPage,
});
